import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FilterChips = ({ filters, categories = [], onRemoveFilter, onClearAll }) => {
  const getCategoryLabel = () => {
    if (!filters?.propertyType || filters?.propertyType === 'all') return null;
    const found = categories.find(cat => String(cat.id) === String(filters.propertyType));
    return found?.category || found?.name || filters.propertyType;
  };

  const getActiveFilters = () => {
    const active = [];

    if (filters?.location) {
      active?.push({ key: 'location', label: filters?.location, value: filters?.location });
    }

    if (filters?.propertyType && filters?.propertyType !== 'all') {
      const categoryLabel = getCategoryLabel();
      active?.push({
        key: 'propertyType',
        label: categoryLabel || filters?.propertyType,
        value: filters?.propertyType
      });
    }

    if (filters?.priceRange && (filters?.priceRange?.[0] !== 0 || filters?.priceRange?.[1] !== 500000000)) {
      const min = filters?.priceRange?.[0] > 0 ? `UGX ${parseInt(filters?.priceRange?.[0])?.toLocaleString()}` : '';
      const max = filters?.priceRange?.[1] < 500000000 ? `UGX ${parseInt(filters?.priceRange?.[1])?.toLocaleString()}` : '';
      const priceLabel = min && max ? `${min} - ${max}` : min ? `${min}+` : `Up to ${max}`;
      active?.push({ key: 'priceRange', label: `Price: ${priceLabel}`, value: filters?.priceRange });
    }

    if (filters?.bedrooms && filters?.bedrooms !== 'any') {
      active?.push({
        key: 'bedrooms',
        label: `${filters?.bedrooms}+ Bed`,
        value: filters?.bedrooms
      });
    }

    if (filters?.bathrooms && filters?.bathrooms !== 'any') {
      active?.push({
        key: 'bathrooms',
        label: `${filters?.bathrooms}+ Bath`,
        value: filters?.bathrooms
      });
    }

    if (filters?.sqftRange && (filters?.sqftRange?.[0] !== 0 || filters?.sqftRange?.[1] !== 10000)) {
      const min = filters?.sqftRange?.[0] > 0 ? `${parseInt(filters?.sqftRange?.[0])?.toLocaleString()}` : '';
      const max = filters?.sqftRange?.[1] < 10000 ? `${parseInt(filters?.sqftRange?.[1])?.toLocaleString()}` : '';
      const sqftLabel = min && max ? `${min} - ${max} sqft` : min ? `${min}+ sqft` : `Up to ${max} sqft`;
      active?.push({ key: 'sqftRange', label: `Area: ${sqftLabel}`, value: filters?.sqftRange });
    }

    if (filters?.yearBuiltRange && (filters?.yearBuiltRange?.[0] !== 1900 || filters?.yearBuiltRange?.[1] !== new Date().getFullYear())) {
      const min = filters?.yearBuiltRange?.[0] > 1900 ? filters?.yearBuiltRange?.[0] : '';
      const max = filters?.yearBuiltRange?.[1] < new Date().getFullYear() ? filters?.yearBuiltRange?.[1] : '';
      const yearBuiltLabel = min && max ? `${min} - ${max}` : min ? `${min}+` : `Up to ${max}`;
      active?.push({ key: 'yearBuiltRange', label: `Year Built: ${yearBuiltLabel}`, value: filters?.yearBuiltRange });
    }

    if (filters?.status && filters?.status !== 'all') {
      const statusLabel = filters?.status === 'sell' ? 'For Sale' :
                         filters?.status === 'rent' ? 'For Rent' : filters?.status;
      active?.push({ key: 'status', label: statusLabel, value: filters?.status });
    }

    if (filters?.amenities && filters?.amenities?.length > 0) {
      filters?.amenities?.forEach(amenity => {
        active?.push({ key: 'amenities', label: `Amenity: ${amenity}`, value: amenity });
      });
    }

    if (filters?.features && filters?.features?.length > 0) {
      filters?.features?.forEach(feature => {
        active?.push({ key: 'features', label: `Feature: ${feature}`, value: feature });
      });
    }

    if (filters?.recentlyAdded && filters?.recentlyAdded !== 'any') {
      const recentlyAddedLabel = {
        '24h': 'Last 24 Hours',
        '7d': 'Last 7 Days',
        '30d': 'Last 30 Days',
      }[filters?.recentlyAdded] || filters?.recentlyAdded;
      active?.push({ key: 'recentlyAdded', label: `Added: ${recentlyAddedLabel}`, value: filters?.recentlyAdded });
    }

    if (filters?.hasVirtualTour) {
      active?.push({ key: 'hasVirtualTour', label: 'Virtual Tour', value: true });
    }

    if (filters?.distanceFromCity && filters?.distanceFromCity !== 50) {
      active?.push({ key: 'distanceFromCity', label: `Distance: < ${filters?.distanceFromCity} km`, value: filters?.distanceFromCity });
    }

    if (filters?.hoaFeeMax) {
      active?.push({ key: 'hoaFeeMax', label: `Max HOA: UGX ${parseInt(filters?.hoaFeeMax)?.toLocaleString()}`, value: filters?.hoaFeeMax });
    }

    if (filters?.lotSizeMin) {
      active?.push({ key: 'lotSizeMin', label: `Min Lot Size: ${parseInt(filters?.lotSizeMin)?.toLocaleString()} sqft`, value: filters?.lotSizeMin });
    }

    // Dynamic category parameters
    Object.keys(filters).forEach(key => {
      if (key.startsWith('param_') && filters[key]) {
        const paramValue = filters[key];
        if (Array.isArray(paramValue) && paramValue.length > 0) {
          paramValue.forEach(val => {
            active?.push({ key: key, label: `${key.replace('param_', '')}: ${val}`, value: val });
          });
        } else if (!Array.isArray(paramValue)) {
          active?.push({ key: key, label: `${key.replace('param_', '')}: ${paramValue}`, value: paramValue });
        }
      }
    });

    return active;
  };

  const activeFilters = getActiveFilters();

  if (activeFilters?.length === 0) {
    return null;
  }

  const handleRemoveFilter = (filterKey, filterValue) => {
    onRemoveFilter(filterKey, filterValue);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <span className="text-sm font-medium text-muted-foreground mr-2">Active filters:</span>
      {activeFilters?.map((filter, index) => (
        <div
          key={`${filter?.key}-${filter?.value}-${index}`}
          className="inline-flex items-center bg-primary/10 text-primary border border-primary/20 rounded-full px-3 py-1 text-sm"
        >
          <span className="mr-2">{filter?.label}</span>
          <button
            onClick={() => handleRemoveFilter(filter?.key, filter?.value)}
            className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
          >
            <Icon name="X" size={12} />
          </button>
        </div>
      ))}
      {activeFilters?.length > 0 && ( // Changed from > 1 to > 0 to show "Clear all" even with one filter
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearAll}
          className="text-muted-foreground hover:text-foreground ml-2"
        >
          Clear all
        </Button>
      )}
    </div>
  );
};

export default FilterChips;
